<script>
import { getInitials } from '@utils/Format'
import { IS_BETA_PROFESSIONAL_PAGES } from '@modules/professionals'

export default {
  name: 'ProfessionalAvatar',
  props: {
    professional: {
      type: Object,
      default: () => {
        return {}
      },
    },
    elevation: {
      type: [String, Number],
      default: 0,
    },
    color: {
      type: String,
      default: '#D9D9D9',
    },
    textColor: {
      type: String,
      default: 'var(--black)',
    },
    textClass: {
      type: String,
      default: 'text-heading-display',
    },
    size: {
      type: String,
      default: '2rem',
    },
    textSize: {
      type: String,
      default: '',
    },
    border: {
      type: String,
      default: '',
    },
    tooltipSuffix: {
      type: String,
      default: '',
    },
    avatarUrl: {
      type: String,
      default: null,
    },
    hideInitials: {
      type: Boolean,
      default: false,
    },
    showHoverEffect: {
      type: Boolean,
      default: false,
    },
    paramName: {
      type: String,
      default: '',
    },
    viewProfessional: {
      type: Boolean,
      default: false,
    },
    sizeIcon: {
      type: String,
      default: '12px',
    },
  },
  computed: {
    computedAvatarUrl() {
      return (
        this.avatarUrl ||
        (this.professional && this.professional.avatar_url) ||
        null
      )
    },
    name() {
      if (this.paramName && this.paramName.length) {
        return this.professional[this.paramName]
      }

      return this.professional
        ? this.professional.nome || this.professional.name || ''
        : ''
    },
    initials() {
      const nome = this.name

      if (nome.startsWith('+')) {
        return nome && nome.split ? nome.split(' ')[0] : nome
      }

      if (nome && !this.hideInitials) {
        return getInitials(nome)
      }
      if (nome.includes(' ')) {
        return nome.split(' ')[0]
      }
      return nome || ''
    },
    hasDefaultSlot() {
      return this.$slots.default || this.$scopedSlots.default
    },
    hasSlotTooltip() {
      return !!this.$slots['tooltip'] || !!this.$scopedSlots['tooltip']
    },
  },
  methods: {
    openProfessional({ id_profissional, ativo }) {
      if (IS_BETA_PROFESSIONAL_PAGES) {
        this.$showSnack({
          text: 'Sem acesso a página ainda',
          button: true,
        })
        return false
      }
      if (!id_profissional) {
        this.$showSnack({
          text: 'Profissional não encontrado',
          button: true,
        })

        return false
      }

      const nameRoute = ativo
        ? 'professionals.individual'
        : 'professionals.individual.churn'

      this.$router.push({
        name: nameRoute,
        params: { id_profissional: id_profissional || 0 },
      })
    },
  },
}
</script>

<template>
  <v-tooltip top>
    <template v-slot:activator="{ on }">
      <div
        class="professional-avatar rounded-circular full-center"
        :style="{
          width: size,
          height: size,
          'min-width': size,
          'min-height': size,
          'background-color': color,
          outline: border ? `1px solid var(--${border})` : '',
          transition: 'all 0.5s ease',
          width: 'fit-content',
          position: 'relative',
        }"
        :class="{
          [`elevation-${elevation}`]: !!elevation,
          'mouse-hover': showHoverEffect,
        }"
        v-on="on"
      >
        <img
          v-if="computedAvatarUrl"
          :src="computedAvatarUrl"
          :alt="initials"
          class="professional-avatar__img"
        />
        <label
          v-else-if="!hasDefaultSlot"
          :class="{ 'mouse-hover': showHoverEffect, [textClass]: true }"
          :style="{
            'font-size': textSize,
            'white-space': 'nowrap',
            width: 'max-content',
            color: textColor,
          }"
        >
          {{ initials }}
        </label>
        <slot />
        <v-icon
          v-if="viewProfessional"
          style="
            position: absolute;
            right: 0;
            bottom: 0;
            background-color: gray;
            border-radius: 50%;
            padding: 2px;
          "
          class="icon"
          color="white"
          :size="sizeIcon"
          @click="openProfessional(professional)"
        >
          mdi-eye
        </v-icon>
      </div>
    </template>
    <span>
      <template v-if="!hasSlotTooltip">
        {{ name }}
        <template v-if="tooltipSuffix"> | {{ tooltipSuffix }} </template>
      </template>
      <template v-if="hasSlotTooltip">
        <slot name="tooltip" />
      </template>
    </span>
  </v-tooltip>
</template>

<style scoped lang="sass">
.professional-avatar.mouse-hover:hover
  scale: 1.15
  z-index: 5

.professional-avatar__img
  border-radius: 50%
  object-fit: cover
  width: 100%
  height: 100%
</style>
